import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setSelectedFloor } from '@store/slices/monitor.slice';
import { selectSelectedFloorId } from '@store/selectors/monitor.selectors';

/**
 * One-way mirror: `?floor=<id>` → Redux `selectedFloorId`.
 *
 * URL is the source of user intent (deep links, back/forward, copy-paste).
 * Anything that wants to change the selection should call
 * `useSetSelectedFloor`'s setter instead of dispatching directly — the URL
 * write triggers this effect, which keeps Redux in lockstep so existing
 * selectors and components (FloorDrawer, BuildingSvg, etc.) stay correct.
 *
 * The compare-and-dispatch is idempotent, so it survives `resetForSite`
 * wiping the selection (incl. React StrictMode's effect-replay in dev).
 */
export function useFloorUrlSync() {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const selectedFloorId = useAppSelector(selectSelectedFloorId);
  const floorParam = searchParams.get('floor');

  useEffect(() => {
    if (floorParam !== selectedFloorId) {
      dispatch(setSelectedFloor(floorParam));
    }
  }, [floorParam, selectedFloorId, dispatch]);
}

/** Setter to use in place of `dispatch(setSelectedFloor(id))`. Updates the
 *  URL (`replace`, so per-click history doesn't pile up); the URL change is
 *  picked up by `useFloorUrlSync` and propagated into Redux. */
export function useSetSelectedFloor() {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('floor', id);
          else next.delete('floor');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
}
